import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faGithub } from '@fortawesome/free-brands-svg-icons';
import { faComment, faDog, faSatelliteDish } from '@fortawesome/pro-regular-svg-icons';
import { mergeAttributes, Node, nodePasteRule } from '@tiptap/core';
import { VueNodeViewRenderer } from '@tiptap/vue-3';
import RichTextRichLinkView from './RichTextRichLinkView.vue';

/**
 * Attributes for rich links. Not all attributes are used by all link types.
 * Having a consistent schema makes it easier to add new patterns.
 *
 * Common attributes:
 * - href: The URL
 * - data-title: Display text (resolved at paste time)
 * - data-type: Link type (github-pr, chat-channel, etc.)
 * - data-icon: Icon name from ICON_REGISTRY
 * - data-id: Generic identifier (PR number, channel code, etc.)
 *
 * Type-specific attributes (may be null for other types):
 * - data-repository: GitHub repository name
 * - data-channel-code: Chat channel code
 * - data-message-id: Chat message ID
 * - data-project: Project identifier
 */
interface RichLinkAttributes {
  href: string | null;
  target: string;
  'data-title': string | null;
  'data-type': string | null;
  'data-icon': string | null;
  'data-id': string | null;
  'data-repository': string | null;
  'data-channel-code': string | null;
  'data-message-id': string | null;
  'data-project': string | null;
}

const ICON_REGISTRY: Record<string, IconDefinition> = {
  github: faGithub,
  chat: faComment,
  datadog: faDog,
  sentry: faSatelliteDish,
};

interface LinkPattern {
  regex: RegExp;
  type: string;
  icon: string;
  extract: (match: RegExpMatchArray) => {
    title: string;
    id: string;
    repository?: string;
    channelCode?: string;
    messageId?: string;
    project?: string;
  };
}

const LINK_PATTERNS: LinkPattern[] = [
  {
    regex: /https?:\/\/github\.com\/[^/]+\/([^/]+)\/pull\/(\d+)(?:\/[^/]+)?/g,
    type: 'github-pr',
    icon: 'github',
    extract: (match) => ({
      title: `${match[1]}#${match[2]}`,
      id: match[2],
      repository: match[1],
    }),
  },
  {
    regex: /https?:\/\/podos\.io\/chat\/channels\/([a-zA-Z0-9]+)(?:\?.*)?/g,
    type: 'chat-channel',
    icon: 'chat',
    extract: (match) => {
      const [url, channelCode] = match;

      try {
        const urlObj = new URL(url);
        const messageId = urlObj.searchParams.get('message');
        const project = urlObj.searchParams.get('project');

        return {
          title: messageId ? `Channel#${messageId}` : 'Channel',
          id: channelCode,
          channelCode,
          messageId: messageId || undefined,
          project: project || undefined,
        };
      } catch (error) {
        return {
          title: 'Channel',
          id: channelCode,
          channelCode,
        };
      }
    },
  },
  {
    regex: /https?:\/\/app\.datadoghq\.(?:com|eu|us3|us5)\/[^\s)]+/g,
    type: 'datadog',
    icon: 'datadog',
    extract: (match) => {
      const [url] = match;

      try {
        const urlObj = new URL(url);
        const pathSegments = urlObj.pathname.split('/').filter((segment) => segment.length > 0);
        const lastSegment = pathSegments[pathSegments.length - 1];
        const displayText = lastSegment ? `Datadog#${lastSegment}` : 'Datadog';

        return {
          title: displayText,
          id: lastSegment || 'Datadog',
        };
      } catch (error) {
        return {
          title: 'Datadog',
          id: 'datadog',
        };
      }
    },
  },
  {
    regex: /https?:\/\/([\w-]+)\.sentry\.io\/issues\/(?:views\/)?(\d+).*/g,
    type: 'sentry',
    icon: 'sentry',
    extract: (match) => {
      const [url, , id] = match;
      const isView = url.includes('/issues/views/');
      const title = isView ? `Sentry View#${id}` : `Sentry Issue#${id}`;

      return {
        title,
        id,
      };
    },
  },
];

export function buildRichLinks(containerElement: HTMLElement): void {
  containerElement.querySelectorAll('a.tml-rich-link[data-icon]').forEach((element) => {
    if (!(element instanceof HTMLAnchorElement)) {
      return;
    }

    if (element.querySelector('svg')) {
      return;
    }

    const iconName = element.getAttribute('data-icon');

    if (!iconName) {
      return;
    }

    const icon = ICON_REGISTRY[iconName];

    if (!icon) {
      return;
    }

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

    svg.setAttribute('class', 'svg-inline--fa mr-1');
    svg.setAttribute('style', 'width: 14px; height: 14px; flex-shrink: 0;');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('role', 'img');
    svg.setAttribute('viewBox', `0 0 ${icon.icon[0]} ${icon.icon[1]}`);

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');

    path.setAttribute('fill', 'currentColor');
    path.setAttribute('d', icon.icon[4] as string);

    svg.appendChild(path);

    element.classList.add('inline-flex', 'items-center');

    element.insertBefore(svg, element.firstChild);
  });
}

export const RichTextRichLink = Node.create({
  name: 'richLink',

  group: 'inline',
  inline: true,
  content: 'text*',
  atom: true,

  addOptions() {
    return {
      ...this.parent?.(),
      store: null,
    };
  },

  addAttributes() {
    return {
      href: {
        default: null,
        parseHTML: (element) => element.getAttribute('href'),
      },
      target: {
        default: '_blank',
        parseHTML: (element) => element.getAttribute('target'),
      },
      'data-title': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-title'),
      },
      'data-type': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-type'),
      },
      'data-icon': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-icon'),
      },
      'data-id': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-id'),
      },
      'data-repository': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-repository'),
      },
      'data-channel-code': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-channel-code'),
      },
      'data-message-id': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-message-id'),
      },
      'data-project': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-project'),
      },
    };
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          const { selection } = state;
          const { empty, anchor } = selection;

          if (!empty) {
            return false;
          }

          let isRichLink = false;
          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              isRichLink = true;
              tr.insertText('', pos, pos + node.nodeSize);
              return false;
            }
          });

          return isRichLink;
        }),
    };
  },

  parseHTML() {
    return [
      {
        tag: 'a.tml-rich-link',
        getAttrs: (element) => {
          const href = element.getAttribute('href');
          const title = element.getAttribute('data-title');
          const type = element.getAttribute('data-type');
          const icon = element.getAttribute('data-icon');
          const id = element.getAttribute('data-id');
          const repository = element.getAttribute('data-repository');
          const channelCode = element.getAttribute('data-channel-code');
          const messageId = element.getAttribute('data-message-id');
          const project = element.getAttribute('data-project');

          return {
            href,
            target: '_blank',
            'data-title': title,
            'data-type': type,
            'data-icon': icon,
            'data-id': id,
            'data-repository': repository,
            'data-channel-code': channelCode,
            'data-message-id': messageId,
            'data-project': project,
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { 'data-title': title, href } = HTMLAttributes;

    return [
      'a',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        class:
          'tml-rich-link border border-[--tml-chat-message-rich-tag-border-color] bg-[--tml-chat-message-rich-tag-background-color] text-[--tml-chat-message-rich-tag-text-color] px-[--tml-chat-message-rich-tag-padding-x] text-sm rounded-[--tml-chat-message-rich-tag-border-radius] whitespace-nowrap inline-flex items-center',
      }),
      title || href || 'Link',
    ];
  },

  addNodeView() {
    return VueNodeViewRenderer(RichTextRichLinkView);
  },

  addPasteRules() {
    return LINK_PATTERNS.map((pattern) =>
      nodePasteRule({
        find: pattern.regex,
        type: this.type,
        getAttributes: (match): RichLinkAttributes => {
          const [url] = match;
          const extracted = pattern.extract(match);

          let finalTitle = extracted.title;

          if (pattern.type === 'chat-channel' && extracted.channelCode) {
            const store = this.options.store;

            if (store?.channels) {
              const channel = store.channels.find(
                (c: { code: string; name: string }) => c.code === extracted.channelCode,
              );

              if (channel) {
                finalTitle = extracted.messageId
                  ? `${channel.name}#${extracted.messageId}`
                  : channel.name;
              }
            }
          }

          return {
            href: url,
            target: '_blank',
            'data-title': finalTitle,
            'data-type': pattern.type,
            'data-icon': pattern.icon,
            'data-id': extracted.id,
            'data-repository': extracted.repository || null,
            'data-channel-code': extracted.channelCode || null,
            'data-message-id': extracted.messageId || null,
            'data-project': extracted.project || null,
          };
        },
      }),
    );
  },
});
